import * as React from 'react';
import { Button, ButtonProps } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

export interface LoadingButtonProps extends ButtonProps {
  loading?: boolean;
  /** Replaces the label while loading. Without it the label is left alone. */
  loadingText?: string;
}

/**
 * The spinner consumes the leading icon instead of sitting next to it, so a
 * button that already has one keeps its width the moment it starts loading.
 */
const loadingContent = (
  children: React.ReactNode,
  spinner: React.ReactNode,
  loadingText?: string,
): React.ReactNode => {
  if (loadingText !== undefined) {
    return (
      <>
        {spinner}
        {loadingText}
      </>
    );
  }

  const [first, ...rest] = React.Children.toArray(children);
  return (
    <>
      {spinner}
      {React.isValidElement(first) ? rest : children}
    </>
  );
};

const LoadingButton = React.forwardRef<HTMLButtonElement, LoadingButtonProps>(
  ({ loading = false, loadingText, disabled, asChild = false, children, ...props }, ref) => {
    // A generic "loading" announcement on top of loadingText would be read twice.
    const spinner = <Spinner size="sm" label={loadingText === undefined ? undefined : ''} />;

    // asChild routes through Radix Slot, which accepts exactly one child, so the
    // spinner has to go inside that child rather than beside it.
    const slotted =
      asChild && React.isValidElement<{ children?: React.ReactNode }>(children) ? children : null;
    const inner = slotted ? slotted.props.children : children;
    const content = loading ? loadingContent(inner, spinner, loadingText) : inner;

    return (
      <Button
        ref={ref}
        asChild={asChild}
        disabled={disabled || loading}
        aria-busy={loading}
        {...props}
      >
        {slotted ? React.cloneElement(slotted, undefined, content) : content}
      </Button>
    );
  },
);
LoadingButton.displayName = 'LoadingButton';

export { LoadingButton };
